import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { useConfig } from '@/contexts/ConfigContext';
import toast from 'react-hot-toast';
import { ArrowLeft, FileText, Bug, MessageSquare, X } from 'lucide-react';
import { clsx } from 'clsx';
import type { ItemType, ItemPriority, ItemCreate } from '@/types';

const typeOptions: { value: ItemType; label: string; description: string; icon: typeof FileText }[] = [
  {
    value: 'feedback',
    label: 'Feedback',
    description: 'Share thoughts or suggestions about the product',
    icon: MessageSquare,
  },
  {
    value: 'requirement',
    label: 'Feature Request',
    description: 'Request a new feature or capability',
    icon: FileText,
  },
  {
    value: 'bug',
    label: 'Bug Report',
    description: 'Report something that is not working correctly',
    icon: Bug,
  },
];

const priorityOptions: { value: ItemPriority; label: string; description: string }[] = [
  { value: 'low', label: 'Low', description: 'Nice to have, no urgency' },
  { value: 'medium', label: 'Medium', description: 'Important but not blocking' },
  { value: 'high', label: 'High', description: 'Significant impact on work' },
  { value: 'critical', label: 'Critical', description: 'Blocking issue, needs immediate attention' },
];

export function NewItem() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { config } = useConfig();

  const [formData, setFormData] = useState<ItemCreate>({
    type: 'feedback',
    title: '',
    description: '',
    priority: 'medium',
    tags: [],
  });

  const availableTags = config.available_tags || [];

  const createMutation = useMutation({
    mutationFn: (data: ItemCreate) => apiClient.createItem(data),
    onSuccess: (item) => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['item-stats'] });
      queryClient.invalidateQueries({ queryKey: ['recent-activity'] });
      toast.success('Item submitted successfully');
      navigate(`/items/${item.id}`);
    },
    onError: () => {
      toast.error('Failed to submit item');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    createMutation.mutate(formData);
  };

  const toggleTag = (tag: string) => {
    const currentTags = formData.tags || [];
    if (currentTags.includes(tag)) {
      setFormData({
        ...formData,
        tags: currentTags.filter((t) => t !== tag),
      });
    } else {
      setFormData({
        ...formData,
        tags: [...currentTags, tag],
      });
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Back link */}
      <Link
        to="/items"
        className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-6"
      >
        <ArrowLeft className="mr-1 h-4 w-4" />
        Back to items
      </Link>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
            Submit New Item
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Share your feedback, request a feature, or report a bug
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Type selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              What would you like to submit?
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {typeOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, type: option.value })}
                    className={clsx(
                      'relative flex flex-col items-center p-4 rounded-lg border-2 transition-colors',
                      formData.type === option.value
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    )}
                  >
                    <Icon
                      className={clsx(
                        'h-8 w-8 mb-2',
                        formData.type === option.value
                          ? 'text-primary-600 dark:text-primary-400'
                          : 'text-gray-400'
                      )}
                    />
                    <span
                      className={clsx(
                        'text-sm font-medium',
                        formData.type === option.value
                          ? 'text-primary-700 dark:text-primary-300'
                          : 'text-gray-900 dark:text-white'
                      )}
                    >
                      {option.label}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                      {option.description}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Brief summary of your item"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={
                formData.type === 'bug'
                  ? 'Please describe the issue, steps to reproduce, and expected behavior...'
                  : formData.type === 'requirement'
                  ? 'Please describe the feature you would like, and why it would be valuable...'
                  : 'Please share your feedback in detail...'
              }
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Priority
            </label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value as ItemPriority })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {priorityOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} - {option.description}
                </option>
              ))}
            </select>
          </div>

          {/* Tags */}
          {availableTags.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tags (optional)
              </label>
              <div className="flex flex-wrap gap-2">
                {availableTags.map((tag) => {
                  const isSelected = formData.tags?.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      className={clsx(
                        'inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                        isSelected
                          ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-200 ring-2 ring-primary-500'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      )}
                    >
                      {tag}
                      {isSelected && (
                        <X className="ml-1.5 h-3.5 w-3.5" />
                      )}
                    </button>
                  );
                })}
              </div>
              {formData.tags && formData.tags.length > 0 && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Selected: {formData.tags.join(', ')}
                </p>
              )}
            </div>
          )}

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Link
              to="/items"
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createMutation.isPending ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
